// A score item is always in exactly one of three states, matching the API
// contract:
//
//   answered        a value on the heading's scale   notApplicable: false
//   not applicable  score null                       notApplicable: true
//   not answered    score null                       notApplicable: false
//
// The range a score is valid on is the *heading's* scoring scale, not a
// hardcoded 1-5: a POMMe-Health heading is scored 1-5, an ICF one 0-4, a
// ten-point one 1-10. Every helper here therefore takes the scale as its last
// argument and falls back to the default 1-5 scale when it is not given, which
// is exactly what a heading that names no scale is scored on.
//
// Every payload goes through toScorePayload and every value read back from the
// API goes through toScoreValue.

import {
  DEFAULT_SCORING_SCALE,
  NOT_APPLICABLE_LABEL,
  getScaleValues,
  isValidScaleScore,
} from "./scoringScale";

export { NOT_APPLICABLE_LABEL };

// The 1-5 range headings fall back to. Kept as named values for the few places
// that still need to talk about the default scale in the abstract.
export const DEFAULT_SCORE_VALUES = getScaleValues(DEFAULT_SCORING_SCALE);

// Numeric score for a raw value (the API sends scores as strings), or null when
// the value is missing or is not a point on the scale.
export const toScoreValue = (value, scale) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed)) return null;
  return isValidScaleScore(scale, parsed) ? parsed : null;
};

export const isNotApplicable = (item) => item?.notApplicable === true;

// True when the user made a choice — either a score or an explicit N/A.
export const isAnswered = (item, scale) =>
  isNotApplicable(item) || toScoreValue(item?.score, scale) !== null;

// True only when there is a real number on the scale. N/A and unanswered items
// are both false — use this to decide whether a record is worth showing.
export const hasScore = (item, scale) => toScoreValue(item?.score, scale) !== null;

// What to show for a score item: "N/A" when not applicable, the number when
// answered, and the `unanswered` placeholder otherwise.
export const formatScore = (item, unanswered = "", scale) => {
  if (isNotApplicable(item)) return NOT_APPLICABLE_LABEL;
  const score = toScoreValue(item?.score, scale);
  return score === null ? unanswered : score;
};

// Same, for standalone values (medians, team scores) that carry no N/A flag.
export const formatScoreValue = (
  value,
  fallback = NOT_APPLICABLE_LABEL,
  scale
) => {
  const score = toScoreValue(value, scale);
  return score === null ? fallback : score;
};

// A single scores[] entry in the shape the API expects. N/A and a score are
// mutually exclusive, so the score is always cleared for an N/A item.
export const toScorePayload = (item = {}, scale) => {
  const notApplicable = isNotApplicable(item);
  const payload = {
    subheadingId: item.subheadingId,
    score: notApplicable ? null : toScoreValue(item.score, scale),
    notApplicable,
  };
  if (item.comment !== undefined) {
    payload.comment = item.comment ?? "";
  }
  return payload;
};

// A scores[] payload. The list is flat but spans several headings, so the scale
// is resolved per row: pass the resolver from scaleResolverFor(headings), or
// nothing when every row is on the default scale.
export const toScoresPayload = (items = [], resolveScale) =>
  (items || []).map((item) =>
    toScorePayload(
      item,
      typeof resolveScale === "function"
        ? resolveScale(item?.subheadingId)
        : resolveScale
    )
  );

// A blank item — used when initialising a checklist and when clearing a row.
export const emptyScoreItem = (subheadingId, comment = "") => ({
  subheadingId,
  score: null,
  notApplicable: false,
  comment,
});
